import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Users, DollarSign, Briefcase, GraduationCap, Heart, TrendingUp, Shield, Star, CheckCircle, XCircle } from 'lucide-react';
import { countriesAPI } from '@/lib/api';
import AnimatedTour from '@/components/AnimatedTour';

const CountryDetailPage = () => {
  const { id } = useParams();
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const response = await countriesAPI.getById(id);
        setCountry(response.data);
      } catch (error) {
        console.error('Failed to fetch country:', error);
        setCountry(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCountry();
  }, [id]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  if (!country) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-zinc-400">کشور یافت نشد</div></div>;
  }

  return (
    <div className="min-h-screen" data-testid="country-detail-page">
      {/* Hero Section */}
      <section
        className="relative h-[400px] flex items-center justify-center"
        style={{
          backgroundImage: `url(${country.image_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center px-4">
          <div className="text-6xl mb-4">{country.flag_emoji}</div>
          <h1 className="text-4xl sm:text-5xl font-light text-white mb-2" data-testid="country-name">{country.name_fa}</h1>
          <p className="text-xl text-zinc-300">{country.capital_fa}</p>
        </div>
      </section>

      {/* Animated Tour Section */}
      {country.popular_cities && country.popular_cities.length > 0 && (
        <div className="container-custom py-12">
          <AnimatedTour cities={country.popular_cities} countryName={country.name_fa} />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Animated Tour Section */}
        {country.major_cities && country.major_cities.length > 0 && (
          <div className="mb-12">
            <AnimatedTour cities={country.major_cities} countryName={country.name_fa} />
          </div>
        )}
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="glass-effect border border-white/10 rounded-xl p-6 text-center">
            <div className="text-[#D4AF37] mb-2"><Star size={24} className="mx-auto" /></div>
            <div className="text-2xl font-semibold text-white mb-1">{country.quality_of_life_score}</div>
            <div className="text-sm text-zinc-400">کیفیت زندگی</div>
          </div>
          <div className="glass-effect border border-white/10 rounded-xl p-6 text-center">
            <div className="text-[#D4AF37] mb-2"><Shield size={24} className="mx-auto" /></div>
            <div className="text-2xl font-semibold text-white mb-1">{country.security_score}</div>
            <div className="text-sm text-zinc-400">امنیت</div>
          </div>
          <div className="glass-effect border border-white/10 rounded-xl p-6 text-center">
            <div className="text-[#D4AF37] mb-2"><GraduationCap size={24} className="mx-auto" /></div>
            <div className="text-2xl font-semibold text-white mb-1">{country.education_score}</div>
            <div className="text-sm text-zinc-400">آموزش</div>
          </div>
          <div className="glass-effect border border-white/10 rounded-xl p-6 text-center">
            <div className="text-[#D4AF37] mb-2"><Heart size={24} className="mx-auto" /></div>
            <div className="text-2xl font-semibold text-white mb-1">{country.healthcare_score}</div>
            <div className="text-sm text-zinc-400">بهداشت</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Basic Info */}
            <div className="glass-effect border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-medium text-white mb-6">اطلاعات کلی</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Users className="text-[#D4AF37] mt-1" size={20} />
                  <div>
                    <div className="text-sm text-zinc-400 mb-1">جمعیت</div>
                    <div className="text-white font-medium">{country.population}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="text-[#D4AF37] mt-1" size={20} />
                  <div>
                    <div className="text-sm text-zinc-400 mb-1">زبان رسمی</div>
                    <div className="text-white font-medium">{country.official_language}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <DollarSign className="text-[#D4AF37] mt-1" size={20} />
                  <div>
                    <div className="text-sm text-zinc-400 mb-1">واحد پول</div>
                    <div className="text-white font-medium">{country.currency}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <TrendingUp className="text-[#D4AF37] mt-1" size={20} />
                  <div>
                    <div className="text-sm text-zinc-400 mb-1">آب‌وهوا</div>
                    <div className="text-white font-medium">{country.climate}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Info */}
            <div className="glass-effect border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-medium text-white mb-6">هزینه‌ها و درآمد</h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-zinc-400">هزینه زندگی ماهانه</span>
                  <span className="text-white font-medium">{country.avg_living_cost}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-zinc-400">اجاره خانه ماهانه</span>
                  <span className="text-white font-medium">{country.avg_rent}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-zinc-400">متوسط حقوق سالانه</span>
                  <span className="text-white font-medium">{country.avg_salary}</span>
                </div>
              </div>
            </div>

            {/* Best Cities */}
            <div className="glass-effect border border-white/10 rounded-2xl p-8">
              <h2 className="text-2xl font-medium text-white mb-6">بهترین شهرها</h2>
              <div className="grid grid-cols-2 gap-4">
                {country.best_cities.map((city) => (
                  <div key={`city-${country.id}-${city}`} className="flex items-center gap-2 text-zinc-300">
                    <MapPin size={16} className="text-[#D4AF37]" />
                    <span>{city}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Pros & Cons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="glass-effect border border-white/10 rounded-2xl p-8">
                <h3 className="text-xl font-medium text-white mb-4 flex items-center gap-2">
                  <CheckCircle className="text-green-500" size={20} />
                  مزایا
                </h3>
                <ul className="space-y-3">
                  {country.pros.map((pro) => (
                    <li key={pro} className="flex items-start gap-2 text-sm text-zinc-300">
                      <span className="text-[#D4AF37] mt-1">•</span>
                      <span>{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="glass-effect border border-white/10 rounded-2xl p-8">
                <h3 className="text-xl font-medium text-white mb-4 flex items-center gap-2">
                  <XCircle className="text-red-500" size={20} />
                  معایب
                </h3>
                <ul className="space-y-3">
                  {country.cons.map((con) => (
                    <li key={con} className="flex items-start gap-2 text-sm text-zinc-300">
                      <span className="text-[#D4AF37] mt-1">•</span>
                      <span>{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Residence Conditions */}
            <div className="glass-effect border border-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-medium text-white mb-4">شرایط اقامت</h3>
              <p className="text-sm text-zinc-300 leading-relaxed">{country.residence_conditions}</p>
            </div>

            {/* Citizenship */}
            <div className="glass-effect border border-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-medium text-white mb-4">شرایط تابعیت</h3>
              <p className="text-sm text-zinc-300 leading-relaxed">{country.citizenship_conditions}</p>
            </div>

            {/* Top Fields */}
            <div className="glass-effect border border-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-medium text-white mb-4">رشته‌های برتر</h3>
              <div className="space-y-2">
                {country.top_fields.map((field) => (
                  <div key={field} className="text-sm text-zinc-300 py-2 border-b border-white/5 last:border-0">
                    {field}
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <Link
              to="/contact"
              data-testid="consultation-cta"
              className="block w-full px-6 py-4 bg-[#D4AF37] text-black rounded-xl text-center font-medium hover:bg-[#F3E5AB] transition-colors"
            >
              درخواست مشاوره برای {country.name_fa}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountryDetailPage;